import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Home, Search, ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';

export default function NotFoundPage() {
    const navigate = useNavigate();
    const canvasRef = useRef(null);

    // Floating particles animation
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        const particles = Array.from({ length: 30 }, () => ({
            x: Math.random() * canvas.width,
            y: Math.random() * canvas.height,
            r: Math.random() * 4 + 1,
            dx: (Math.random() - 0.5) * 0.6,
            dy: (Math.random() - 0.5) * 0.6,
            alpha: Math.random() * 0.5 + 0.1,
        }));

        let raf;
        const draw = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            particles.forEach(p => {
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(222, 65, 176, ${p.alpha})`;
                ctx.fill();
                p.x += p.dx; p.y += p.dy;
                if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
                if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
            });
            raf = requestAnimationFrame(draw);
        };
        draw();

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        window.addEventListener('resize', resize);
        return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
    }, []);

    return (
        <main className="main" style={{ position: 'relative', overflow: 'hidden', minHeight: '100vh', paddingTop: 0 }}>
            <SEO
                title="404 - Page Not Found | ATAM Connect"
                description="The page you're looking for doesn't exist. Return to ATAM Connect to discover Singapore's Indian Heritage and Arts events."
            />

            {/* Particle canvas */}
            <canvas ref={canvasRef} style={{
                position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none'
            }} />

            {/* Soft radial glow */}
            <div style={{
                position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
                background: 'radial-gradient(ellipse at 30% 40%, rgba(222,65,176,0.10) 0%, transparent 60%), radial-gradient(ellipse at 70% 70%, rgba(254,205,109,0.10) 0%, transparent 60%)'
            }} />

            <div style={{
                position: 'relative', zIndex: 1,
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', minHeight: '100vh',
                textAlign: 'center', padding: '40px 24px'
            }}>
                {/* 404 Big Number */}
                <div style={{
                    fontSize: 'clamp(100px, 20vw, 200px)',
                    fontWeight: 900,
                    lineHeight: 1,
                    letterSpacing: '-0.04em',
                    background: 'linear-gradient(135deg, #de41b0 0%, #fecd6d 60%, #ff8a65 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    marginBottom: 0,
                    filter: 'drop-shadow(0 8px 32px rgba(222,65,176,0.25))',
                    animation: 'nf-float 3s ease-in-out infinite',
                }}>
                    404
                </div>

                {/* Divider line */}
                <div style={{
                    width: 60, height: 4, borderRadius: 2,
                    background: 'linear-gradient(90deg, #de41b0, #fecd6d)',
                    margin: '20px auto 28px',
                }} />

                <h1 style={{
                    fontSize: 'clamp(22px, 4vw, 38px)',
                    fontWeight: 800, marginBottom: 12,
                    color: 'var(--text-primary)',
                }}>
                    Oops! Page Not Found
                </h1>

                <p style={{
                    fontSize: '1rem', color: 'var(--text-secondary)',
                    maxWidth: 440, marginBottom: 40, lineHeight: 1.7,
                }}>
                    The page you're trying to reach doesn't exist or may have been moved.
                    Head back to explore Singapore's Indian Heritage &amp; Arts events.
                </p>

                {/* Buttons */}
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 40 }}>
                    <Link to="/" className="btn btn-primary" style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '12px 26px', fontSize: '0.95rem',
                    }}>
                        <Home size={18} /> Go Home
                    </Link>
                    <Link to="/events" className="btn btn-secondary" style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '12px 26px', fontSize: '0.95rem',
                    }}>
                        <Search size={18} /> Browse Events
                    </Link>
                    <button onClick={() => navigate(-1)} className="btn btn-secondary" style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '12px 26px', fontSize: '0.95rem',
                    }}>
                        <ArrowLeft size={18} /> Go Back
                    </button>
                </div>

                {/* Quick links */}
                <div style={{
                    display: 'flex', gap: 24, flexWrap: 'wrap',
                    justifyContent: 'center', opacity: 0.55, fontSize: '0.85rem'
                }}>
                    {[['About', '/about'], ['Partners', '/partners'], ['Contact', '/contact']].map(([label, to]) => (
                        <Link key={to} to={to} style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}
                            onMouseEnter={e => e.target.style.color = '#de41b0'}
                            onMouseLeave={e => e.target.style.color = 'var(--text-secondary)'}
                        >{label}</Link>
                    ))}
                </div>
            </div>

            {/* Keyframe for float animation */}
            <style>{`
                @keyframes nf-float {
                    0%, 100% { transform: translateY(0px); }
                    50%       { transform: translateY(-12px); }
                }
            `}</style>
        </main>
    );
}
